import { Account } from '@ambire-common/interfaces/account'
import { Key } from '@ambire-common/interfaces/keystore'
import { i18nKey } from '@common/config/localization/constants'

export type PrivateModeReason =
  /** Account already has a Railgun wallet — toggle is enabled. */
  | 'available'
  /** Account has a seed-derived internal key but Railgun wallet hasn't been created yet. */
  | 'creatable'
  /** Engine or wallet creation in progress — show spinner. */
  | 'initializing'
  /** Engine init failed — surface a retry affordance via the tooltip. */
  | 'failed'
  /** Account was imported from a raw private key (no seed phrase available to derive Railgun keys). */
  | 'no-seed'
  /**
   * The account's key came from a seed, but that seed is not on this device: it was
   * synced from another device without its recovery phrase, or the phrase was
   * deleted here afterwards. Syncing again with the phrase (or importing it) fixes it.
   */
  | 'seed-not-on-device'
  /** All associated keys are external (trezor/ledger/lattice/walletconnect). */
  | 'hardware'
  /** Account has no associated signing keys (view-only or unimported). */
  | 'view-only'

/** Subset of `Key` we actually inspect — keeps the helper testable without depending on the full keystore type. */
export type KeyForReason = Pick<Key, 'addr' | 'type'> & {
  meta?: { fromSeedId?: string; seedNotSynced?: boolean } & Record<string, unknown>
}

export const computePrivateModeReason = ({
  account,
  keys,
  seedIds,
  hasRailgunWallet
}: {
  account: Pick<Account, 'addr' | 'associatedKeys'> | null | undefined
  keys: KeyForReason[]
  /**
   * Ids of the seeds stored on this device (`keystoreState.seeds`). A key's
   * `fromSeedId` only counts when its seed is still here: deleting a seed does not
   * unlink the keys derived from it. Omit to trust `fromSeedId` as is.
   */
  seedIds?: readonly string[]
  hasRailgunWallet: boolean
}): PrivateModeReason => {
  // View-only takes precedence over `hasRailgunWallet`: a watch-only account can
  // never sign, so private mode must stay blocked even if a stale Railgun wallet
  // row is persisted for its address (e.g. the address was previously a full
  // account). Checking this first prevents 'available' from leaking through.
  if (!account) return 'view-only'

  const associated = new Set(account.associatedKeys.map((a) => a.toLowerCase()))
  const accountKeys = keys.filter((k) => associated.has(k.addr.toLowerCase()))

  if (accountKeys.length === 0) return 'view-only'

  // The seed comes before `hasRailgunWallet`, for the same reason as view-only:
  // the persisted wallet rows are keyed by address and never pruned, so a row
  // can outlive the seed (e.g. accounts synced from another device without
  // their recovery phrase). Without the seed the wallet can't be loaded anyway.
  const seedsOnDevice = seedIds ? new Set(seedIds) : null
  const internalKeys = accountKeys.filter((k) => k.type === 'internal')
  const hasSeedKey = internalKeys.some(
    (k) =>
      typeof k.meta?.fromSeedId === 'string' &&
      (!seedsOnDevice || seedsOnDevice.has(k.meta.fromSeedId))
  )
  if (hasSeedKey) return hasRailgunWallet ? 'available' : 'creatable'

  // Seed-derived key whose seed isn't here (synced without it, or deleted since).
  const hasKeyMissingItsSeed = internalKeys.some(
    (k) => k.meta?.seedNotSynced === true || typeof k.meta?.fromSeedId === 'string'
  )
  if (hasKeyMissingItsSeed) return 'seed-not-on-device'

  // Internal key but no seed → user imported a raw private key.
  if (internalKeys.length) return 'no-seed'

  // Only external (hardware / walletconnect) keys remain.
  return 'hardware'
}

/**
 * True when the private wallet can NEVER be created for this account — no retry
 * or setup step will help (raw private-key import, seed not on this device, hardware, or view-only). Used
 * to permanently disable the shield/unshield/send entry points. Transient states
 * ('creatable' / 'initializing' / 'failed') are deliberately excluded: private
 * mode becomes available for those, so their entry points stay enabled.
 */
export const isPrivateModeBlocked = (reason: PrivateModeReason): boolean =>
  reason === 'no-seed' ||
  reason === 'seed-not-on-device' ||
  reason === 'hardware' ||
  reason === 'view-only'

// i18n keys — translate at render with t(PRIVATE_MODE_TOOLTIP[reason]).
export const PRIVATE_MODE_TOOLTIP: Record<Exclude<PrivateModeReason, 'available'>, string> = {
  creatable: i18nKey('Setting up your private wallet…'),
  initializing: i18nKey('Setting up your private wallet…'),
  failed: i18nKey("Couldn't set up your private wallet. Click to retry."),
  'no-seed': i18nKey(
    'Private wallet requires a seed phrase. This account was imported from a private key, so a Railgun viewing key cannot be derived.'
  ),
  'seed-not-on-device': i18nKey(
    'Private wallet requires the recovery phrase this account comes from, and it is not on this device. Sync the account again with its recovery phrase, or import the phrase.'
  ),
  hardware: i18nKey('Private wallet is not available for hardware wallets.'),
  'view-only': i18nKey('Private wallet is not available for view-only accounts.')
}
